/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: activity_statuses entity configuration
 * Generator: scripts/generate-api-clients/generate-api-clients.ts
 *
 * This file contains the API client for the activity_statuses table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the activity_statuses API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, organization_id, name, description, color, display_order, is_terminal, is_active, created_at, updated_at, created_by, category_id]
 * Searchable fields: []
 * Filterable fields: [id, organization_id, is_terminal, is_active, category_id]
 */

import { httpClient } from '../http-client.js';
import { globalSearchConfig } from '../../../config/search/global-search-config.js';
import { matchesAllTerms } from '../../../config/search/search-utils.js';
import type {
  ActivityStatusesRow,
  ActivityStatusesInsert,
  ActivityStatusesUpdate
} from '../generated_schemas/generated-activity_statuses-schema.js';
import { ActivityStatusesQueryOptions } from '../generated_schemas/generated-activity_statuses-schema.js';
export type { ActivityStatusesQueryOptions };

// Internal response type with non-null data (not exported to avoid conflicts with schema's Response type)
interface ActivityStatusesClientResponse {
  data: ActivityStatusesRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for activity_statuses table operations
 *
 * @description Provides type-safe access to activity_statuses API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class ActivityStatusesClient {
  private readonly basePath = '/activity-statuses';

  /**
   * Get all activity_statuses records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of activity_statuses records
   */
  async getAll(options?: ActivityStatusesQueryOptions): Promise<ActivityStatusesRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<ActivityStatusesClientResponse>(url);
    return response.data;
  }

  /**
   * Get a single activity_statuses record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the activity_statuses record
   */
  async getById(id: string): Promise<ActivityStatusesRow> {
    const response = await httpClient.get<{
      data: ActivityStatusesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new activity_statuses record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created activity_statuses record
   */
  async create(data: ActivityStatusesInsert): Promise<ActivityStatusesRow> {
    const response = await httpClient.post<{
      data: ActivityStatusesRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing activity_statuses record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated activity_statuses record
   */
  async update(id: string, data: ActivityStatusesUpdate): Promise<ActivityStatusesRow> {
    const response = await httpClient.put<{
      data: ActivityStatusesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a activity_statuses record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple activity_statuses records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created activity_statuses records
   */
  async createMany(data: ActivityStatusesInsert[]): Promise<ActivityStatusesRow[]> {
    const response = await httpClient.post<{
      data: ActivityStatusesRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple activity_statuses records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search activity_statuses with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    query: string,
    columns?: string[],
    options?: ActivityStatusesQueryOptions
  ): Promise<ActivityStatusesClientResponse> {
    throw new Error('Search operations are not available for activity_statuses - no searchable fields configured');
  }

  /**
   * Get count of activity_statuses records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a activity_statuses record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'activity_statuses',
      publicFields: ['id', 'organization_id', 'name', 'description', 'color', 'display_order', 'is_terminal', 'is_active', 'created_at', 'updated_at', 'created_by', 'category_id'],
      searchableFields: [],
      filterableFields: ['id', 'organization_id', 'is_terminal', 'is_active', 'category_id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['name', 'display_order'],
    };
  }
}

// Export singleton instance for convenience
export const activityStatusesClient = new ActivityStatusesClient();
